import React from 'react';

// Friendly cell names for the 27 HV × Slope × Consol combinations
export const CELL_DISPLAY_NAMES: Record<string, string> = {
  LO_DOWN_EXPANDING: 'Slow Bleed Out',
  LO_DOWN_NORMAL: 'Quiet Downtrend',
  LO_DOWN_TIGHT: 'Quiet Slide',
  LO_FLAT_EXPANDING: 'Drifting Sideways',
  LO_FLAT_NORMAL: 'Quiet Range',
  LO_FLAT_TIGHT: 'Tight Consolidation',
  LO_UP_EXPANDING: 'Gentle Breakout',
  LO_UP_NORMAL: 'Steady Uptrend',
  LO_UP_TIGHT: 'Compressed Uptrend',
  MED_DOWN_EXPANDING: 'Widening Decline',
  MED_DOWN_NORMAL: 'Normal Downtrend',
  MED_DOWN_TIGHT: 'Compressed Pullback',
  MED_FLAT_EXPANDING: 'Range Widening',
  MED_FLAT_NORMAL: 'Normal Chop',
  MED_FLAT_TIGHT: 'Squeeze Setup',
  MED_UP_EXPANDING: 'Accelerating Rally',
  MED_UP_NORMAL: 'Healthy Uptrend',
  MED_UP_TIGHT: 'Squeezed Uptrend',
  HI_DOWN_EXPANDING: 'Panic Selloff',
  HI_DOWN_NORMAL: 'Volatile Decline',
  HI_DOWN_TIGHT: 'Compressed Drop',
  HI_FLAT_EXPANDING: 'Wild Chop',
  HI_FLAT_NORMAL: 'Volatile Range',
  HI_FLAT_TIGHT: 'Volatile Squeeze',
  HI_UP_EXPANDING: 'Blowoff Rally',
  HI_UP_NORMAL: 'Hot Uptrend',
  HI_UP_TIGHT: 'Squeezed Rally',
};

export const displayCell = (cellId: string | null | undefined): string => {
  if (!cellId) return 'Unknown setup';
  return CELL_DISPLAY_NAMES[cellId] ?? cellId.replace(/_/g, ' · ');
};

// Parametric icon encoding the 3-dimensional setup (HV × Slope × Consol):
//   HV     → line roughness (smooth / wavy / jagged)
//   Slope  → line direction (down / flat / up)
//   Consol → noise envelope along line (tight = jagged→smooth, expanding = smooth→jagged)
export const SetupIcon: React.FC<{
  cellId: string;
  size?: number;
  color?: string;
}> = ({ cellId, size = 32, color = '#60a5fa' }) => {
  const parts = cellId.split('_');
  if (parts.length !== 3) return null;
  const [hv, slope, consol] = parts;

  const W = 32;
  const H = 32;
  const cy = H / 2;
  const boxW = 24;
  const boxX = (W - boxW) / 2;
  const boxH = consol === 'TIGHT' ? 9 : consol === 'NORMAL' ? 18 : 26;
  const boxY = cy - boxH / 2;

  const slopeAmp = slope === 'UP' ? -5.5 : slope === 'DOWN' ? 5.5 : 0;
  const yStart = cy - slopeAmp;
  const yEnd = cy + slopeAmp;

  const noiseAmp = hv === 'LO' ? 0.5 : hv === 'MED' ? 2.2 : 4.6;
  const nPts = hv === 'LO' ? 9 : hv === 'MED' ? 13 : 19;
  const noiseFreq = hv === 'HI' ? 8 : hv === 'MED' ? 4 : 3;

  let seed = 0;
  for (let i = 0; i < cellId.length; i++) {
    seed = (seed * 31 + cellId.charCodeAt(i)) & 0xffff;
  }

  const envelope = (t: number): number => {
    if (consol === 'TIGHT') return 1.3 - 1.15 * t;
    if (consol === 'EXPANDING') return 0.15 + 1.15 * t;
    return 1.0;
  };

  const points: string[] = [];
  for (let i = 0; i < nPts; i++) {
    const t = i / (nPts - 1);
    const x = boxX + 2 + t * (boxW - 4);
    const baseY = yStart + (yEnd - yStart) * t;
    const endTaper = Math.min(1, Math.min(t, 1 - t) * 8);
    const noise =
      noiseAmp *
      envelope(t) *
      endTaper *
      Math.sin(t * Math.PI * noiseFreq + seed * 0.013);
    points.push(`${x.toFixed(1)},${(baseY + noise).toFixed(1)}`);
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${W} ${H}`}
      style={{ flexShrink: 0, display: 'block' }}
      aria-label={displayCell(cellId)}
    >
      <rect
        x={boxX}
        y={boxY}
        width={boxW}
        height={boxH}
        fill="none"
        stroke={color}
        strokeWidth={0.5}
        strokeDasharray="1.4 1.8"
        opacity={0.3}
      />
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
